import React, {Component, Fragment} from 'react'
import StyleSheet from 'styles'
import PropTypes from 'prop-types'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import {humanize, authorizedLink, camelize} from 'utils'

export class IdentityVerificationDetails extends Component {
  static propTypes = {
    personKyc: PropTypes.object
  }

  state = {tab: "document_data"}

  get verificationResponse() {
    return this.props.personKyc.latestVerification.response.details
  }

  get documentData() {
    const data = this.verificationResponse.extracted
    return Object.keys(data).map(name => ({name, value: data[name]})).filter(item => item.value)
  }

  get recognition() {
    const {card: {asfCheck: {documentAuthenticity}}, faceScan: {confidence, liveness: {result: liveness}} = {}} = this.verificationResponse
    const data = {confidence, liveness, documentAuthenticity}
    return Object.keys(data).map(name => ({name, value: `${data[name]}`}))
  }

  get watchlist() {
    const {centrix: {result: {pepWatchlist: data = {}} = {}} = {}} = this.verificationResponse
    return Object.keys(data).map(name => ({name, value: `${data[name]}`})).filter(item => item.value)
  }

  renderDocumentData = () => {
    const {images = {}} = this.verificationResponse.card
    return (
      <div className={styles.flexTab}>
        <dl>
          {this.documentData.map(({name, value}) =>
            <Fragment>
              <dt>{humanize(name)}</dt>
              <dd>{value}</dd>
            </Fragment>
          )}
        </dl>
        <div className={styles.images}>
          {images.front &&
          <div className={styles.idImage}>
            <img src={authorizedLink(`${attachmentPath}/${new URL(images.front).pathname}`)} alt="id front" />
            <span>Front</span>
          </div>
          }
          {images.back &&
          <div className={styles.idImage}>
            <img src={authorizedLink(`${attachmentPath}/${new URL(images.back).pathname}`)} alt="id back"/>
            <span>Back</span>
          </div>
          }
        </div>
      </div>
    )
  }

  renderRecognition = () => {
    const {video} = this.verificationResponse.faceScan
    return (
      <div className={styles.flexTab}>
        <dl>
          {this.recognition.map(({name, value}) =>
            <Fragment>
              <dt>{humanize(name)}</dt>
              <dd>
                {(typeof value === 'number') ?
                  <span className={styles.percentage}>
                        <span className={styles(['low', 'med', 'high'][~~(value * 0.0299)])} style={{width: value}}>
                          {value}
                        </span>
                      </span>
                  :
                  <span className={styles[value]}>{value}</span>
                }
              </dd>
            </Fragment>
          )}
        </dl>
        <div className={styles.images}>
          {video &&
          <video controls>
            <source src={authorizedLink(`${attachmentPath}/${new URL(video).pathname}`)} type="video/webm"/>
          </video>
          }
        </div>
      </div>
    )
  }

  renderWatchlist = () =>
    <div className={styles.watchlist}>
      <dl>
        {this.watchlist.map(({name, value}) =>
          <Fragment>
            <dt>{humanize(name)}</dt>
            <dd>{value}</dd>
          </Fragment>
        )}
      </dl>
    </div>

  renderRawData = () =>
    <div className={styles.raw}>
      {JSON.stringify(this.verificationResponse, null, 2)}
    </div>

  render = () =>
    <div className={styles.container}>
      <Tabs fullWidth value={this.state.tab} onChange={(e, tab) => this.setState({tab})}>
        <Tab label="Document Data" value="document_data" classes={tabStyles} />
        <Tab label="Recognition" value="recognition" classes={tabStyles} />
        <Tab label="Watchlist" value="watchlist" classes={tabStyles} />
        <Tab label="Raw Data" value="raw_data" classes={tabStyles} />
      </Tabs>

      {this[camelize(`render_${this.state.tab}`)]()}
    </div>

}

export default IdentityVerificationDetails

const attachmentPath = '/api/attachments/identity_verification'

const tabStyles = StyleSheet({
  root: {
    '@media(min-width: 960px)': {
      minWidth: 133,
    }
  },
  labelContainer: {
    paddingLeft: 12,
    paddingRight: 12,
  }
})

const styles = StyleSheet({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& dl': {
      '& dd, dt': {
        float: 'left',
        marginBottom: 10,
      },
      '& dt': {
        fontWeight: 'bold',
        clear: 'both',
        width: 180,
        '&:after': {
          content: "':'"
        },
      },
      '& dd': {
        marginLeft: 0,
      }
    }
  },
  raw: {
    whiteSpace: 'pre',
    fontFamily: 'monospace',
    marginTop: 5,
    padding: 10,
    backgroundColor: '#222',
    color: '#EEE',
    borderRadius: 3,
    overflow: 'auto',
    flex: 1,
  },
  fail: {
    color: '#6AAEAA'
  },
  partial: {
    color: '#f5bd23'
  },
  pass: {
    color: '#90c814'
  },
  percentage: {
    width: 100,
    display: 'inline-block',
    borderRadius: 2,
    backgroundColor: '#EEE',
    '& span': {
      display: 'inline-block',
      textAlign: 'right',
      padding: '2px 2px 0',
      borderRadius: 2,
      boxSizing: 'border-box',
    }
  },
  low: {
    backgroundColor: '#6AAEAA',
  },
  med: {
    backgroundColor: '#f5bd23',
  },
  high: {
    backgroundColor: '#90c814',
  },
  flexTab: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: [0, -10],
    '& dl': {
      flex: 2,
      padding: [0, 10],
      minWidth: 350,
      boxSizing: 'border-box'
    },
    '& $images': {
      flex: 1,
    }
  },
  images: {
    '& video': {
      padding: 10,
      maxWidth: 200,
      minHeight: 150,
    }
  },
  idImage: {
    display: 'inline-block',
    padding: 10,
    '& img': {
      maxWidth: 200,
      maxHeight: 200,
    },
    '& span': {
      display: 'block',
      textAlign: 'center',
      fontSize: '0.9em',
      fontWeight: 'bold',
      padding: 10,
    }
  },
  watchlist: {
    '& dl dt': {
      width: 280,
    }
  }
})